import { useState } from 'react';
import { ESimProfileDTO } from '../Inventory/Profiles/models/eSimProfiles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {
  PanelCard,
  PanelCardContent,
  PanelCardTitle,
} from 'src/common/components/Layouts/PanelCard/PanelCard';
import { SectionGridItem } from 'src/common/components/SectionGrid/SectionGridItem';
import { SectionGridRow } from 'src/common/components/SectionGrid/SectionGridRow';
import { getLabelBaseOnMnoStatus } from 'src/common/components/Sim/constants/mnoStatus';
import { CopyableLink } from 'src/common/components/Table/Link';
import { parseResponseDateAndTime } from 'src/common/utils/parseResponseDate';
import { ESimProfileStatusBadge } from '../Components/ESimProfileStatusBadge';
import { ESimEnableProfileDialog } from '../Inventory/Profiles/actions/ESimEnableProfileDialog';
import { Routes } from 'src/Routes.constants';
import { Button } from 'src/common/components/Buttons/Button';
import { useTranslation } from 'react-i18next';
import { ESimDeleteProfileDialog } from '../Inventory/Profiles/actions/ESimDeleteProfileDialog';
import Divider from '@mui/material/Divider';

type ESimProfilePanelProps = {
  data: ESimProfileDTO;
  esimEid: string;
};

export const ESimProfilePanel = ({ data, esimEid }: ESimProfilePanelProps) => {
  const { t } = useTranslation();
  const [enablePofileDialogOpen, setEnablePofileDialogOpen] = useState(false);
  const [deletePofileDialogOpen, setDeletePofileDialogOpen] = useState(false);

  return (
    <>
      <PanelCard>
        <PanelCardContent>
          <PanelCardTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Typography variant="h3" color="text.secondary">
                  {data.iccid}
                </Typography>
                <ESimProfileStatusBadge value={data.enabled} />
              </Box>
              {!data.enabled && (
                <Box sx={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <Button variant="secondary" onClick={() => setEnablePofileDialogOpen(true)}>
                    {t('products.eSim.inventory.actions.enable.title')}
                  </Button>
                  <Divider orientation="vertical" sx={{ height: 24 }} />
                  <Button variant="secondary" onClick={() => setDeletePofileDialogOpen(true)}>
                    {t('products.eSim.inventory.actions.deleteESimProfile.deleteProfile')}
                  </Button>
                </Box>
              )}
            </Box>
          </PanelCardTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SectionGridRow title={t('simInventory.identificationNumbers')} hideTopBorder>
              <SectionGridItem label={t('common.iccid')} testId={`iccid`}>
                <CopyableLink value={data.iccid} to={`${Routes.simInventory}/${data.iccid}`} />
              </SectionGridItem>
              <SectionGridItem testId={'imsi'} value={data.imsi} label={t('common.imsi')} />
              <SectionGridItem testId={'msisdn'} value={data.msisdn} label={t('common.msisdn')} />
            </SectionGridRow>
            <SectionGridRow
              title={t('simInventory.mobileNetworkOperatorSectionLabel')}
              sx={{ pl: 0 }}
            >
              <SectionGridItem
                testId={'mobileNetworkOperator'}
                value={data.mobileNetworkOperator}
                label={t('simInventory.mobileNetworkOperator')}
              />
              <SectionGridItem
                testId={'commsPlan'}
                value={data.commsPlan}
                label={t('simInventory.commsPlan')}
              />
              <SectionGridItem
                testId={'mnoStatus'}
                value={
                  data.mnoStatus === null
                    ? data.mnoStatus
                    : getLabelBaseOnMnoStatus(data.mnoStatus, t)
                }
                label={t('simInventory.mnoStatus')}
              />

              <SectionGridItem
                testId={'mnoStatusEntryTime'}
                value={parseResponseDateAndTime(data.mnoStatusEntryTime)}
                label={t('simInventory.mnoStatusEntryTime')}
              />
              <SectionGridItem
                testId={'mnoRatePlan'}
                value={data.mnoRatePlan}
                label="MNO Rate Plan"
              />
            </SectionGridRow>
          </Box>
        </PanelCardContent>
      </PanelCard>
      <ESimEnableProfileDialog
        open={enablePofileDialogOpen}
        onClose={() => {
          setEnablePofileDialogOpen(false);
        }}
        data={{ ...data, esimEid }}
      />
      <ESimDeleteProfileDialog
        open={deletePofileDialogOpen}
        onClose={() => {
          setDeletePofileDialogOpen(false);
        }}
        data={{ ...data, esimEid }}
      />
    </>
  );
};
